import { useEffect, useRef, useCallback, useState } from 'preact/hooks'
import { useTranslation } from 'react-i18next'
import {
  MainContentLayout,
  Loader,
  usePageLoader,
  Header,
  Content,
  Text,
  Alert,
  Button,
} from '@/components'
import { DocumentSigningIcon } from '@/components/icons/DocumentSigningIcon'
import { Check } from 'lucide-preact'
import { downloadSignedDocuments } from '@/services/api'
import {
  getAccessTokenType,
  getProcessId,
  getWebReturnUrl,
} from '@/services/session'
import { usePreventBackNavigation } from '@/services/navigation-guard'
import { useSignProcessStatusPolling } from '@/hooks/useSignProcessStatusPolling'
import {
  AccessTokenInfoDtoType,
  ProcessDtoLiteStatus,
} from '@cencert/mob-signature'
import { useConfig } from '@/services/config'
import { useProtectionRoute } from '@/components/navigation/ProtectedRoute'
import { useTimer } from '@/hooks/useTimer'
import { getConfig } from '../../../config'

export function WaitingForSignaturePage() {
  const { t } = useTranslation()
  const { showPageLoader, hidePageLoader } = usePageLoader()
  const { setIsDocumentSigned } = useConfig()
  const { isReloadProtectionActive } = useProtectionRoute()

  const { status, startPolling } = useSignProcessStatusPolling({
    mode: 'waiting-for-signature',
  })

  const [error, setError] = useState<string | null>(null)

  // Blokuj cofanie się w przeglądarce podczas tego etapu procesu
  usePreventBackNavigation()

  const processId = getProcessId()
  const downloadInitiatedRef = useRef(false)

  const isSigned = status === ProcessDtoLiteStatus.SIGNED
  const isHashProcess = getAccessTokenType() === AccessTokenInfoDtoType.HASH
  const webReturnUrl = getWebReturnUrl()

  const { hashProcessShowSuccessView, hashProcessRedirectDelaySeconds } =
    getConfig()

  const isHashProcessReadyToRedirect =
    isSigned && isHashProcess && !!webReturnUrl

  const shouldUseTimer =
    isHashProcessReadyToRedirect &&
    hashProcessShowSuccessView &&
    hashProcessRedirectDelaySeconds > 0

  const { timeLeft, isExpired } = useTimer(
    shouldUseTimer ? hashProcessRedirectDelaySeconds / 60 : undefined
  )

  if (isHashProcessReadyToRedirect) {
    isReloadProtectionActive.current = false
  }

  const handleHashRedirect = () => {
    if (webReturnUrl) {
      isReloadProtectionActive.current = false
      window.location.href = webReturnUrl
    }
  }

  if (
    isHashProcessReadyToRedirect &&
    (!hashProcessShowSuccessView || isExpired)
  ) {
    handleHashRedirect()
    return null
  }

  const handleDownloadDocuments = useCallback(async () => {
    if (!processId) {
      return
    }

    showPageLoader()
    setError(null)

    try {
      await downloadSignedDocuments(processId, {
        onError: error => {
          setError(
            error.response?.data?.detail ??
              t('waitingForSignature.errors.downloadFiles')
          )
        },
      })
    } catch {
      // Error handling could be improved here
    } finally {
      hidePageLoader()
    }
  }, [processId, showPageLoader, hidePageLoader])

  useEffect(() => {
    startPolling()
  }, [])

  useEffect(() => {
    if (isSigned && !isHashProcess) {
      if (!downloadInitiatedRef.current) {
        downloadInitiatedRef.current = true
        setIsDocumentSigned(true)
        handleDownloadDocuments()
      }
    }
  }, [
    status,
    handleDownloadDocuments,
    setIsDocumentSigned,
    isHashProcess,
    isSigned,
  ])

  const getSubtitle = () => {
    if (isSigned) {
      if (isHashProcess) {
        return t('waitingForSignature.hashProcess.completed.subtitle')
      }

      return t('waitingForSignature.completed.subtitle')
    }

    return isHashProcess ? '' : t('waitingForSignature.subtitle')
  }

  return (
    <div className='bg-cencert-bg-gray'>
      <Header />

      <Content showSidebar={true}>
        {error && <Alert variant='error' message={error} />}
        <MainContentLayout
          title={t('waitingForSignature.pageTitle')}
          currentStep={4}
          totalSteps={4}
          {...(isSigned &&
            !isHashProcess && {
              ctaButtonText: t('waitingForSignature.completed.downloadButton'),
              ctaButtonOnClick: handleDownloadDocuments,
              showCTA: true,
            })}
        >
          <div className='pb-9 text-center'>
            {isSigned ? (
              <div className='mx-auto mt-12 mb-8'>
                <div className='w-12 h-12 mx-auto mb-2 rounded-full bg-green-100 flex items-center justify-center'>
                  <Check
                    className='w-8 h-8 text-cencert-success-green'
                    strokeWidth={2}
                  />
                </div>
              </div>
            ) : (
              <Loader
                icon={<DocumentSigningIcon className='w-16 h-20' />}
                className='mx-auto mt-12 mb-8'
              />
            )}

            <Text typographyVariant='heading-4' className='mb-0.5'>
              {isSigned
                ? t('waitingForSignature.completed.title')
                : t('waitingForSignature.title')}
            </Text>

            <Text typographyVariant='body' className='whitespace-pre-line'>
              {getSubtitle()}
            </Text>

            {/* Show redirect section for HASH process with success view enabled */}
            {isHashProcessReadyToRedirect && hashProcessShowSuccessView && (
              <div className='mt-8'>
                {hashProcessRedirectDelaySeconds > 0 && (
                  <Text
                    typographyVariant='body'
                    className='mb-4 text-cencert-text-gray'
                  >
                    {t(
                      'waitingForSignature.hashProcess.completed.autoRedirectMessage'
                    )}{' '}
                    ({timeLeft})
                  </Text>
                )}

                <Button
                  onClick={handleHashRedirect}
                  size='lg'
                  fullWidth={false}
                  className='mx-auto'
                >
                  {t(
                    'waitingForSignature.hashProcess.completed.redirectButton'
                  )}
                </Button>

                <Text
                  typographyVariant='body'
                  className='mt-4 text-sm text-cencert-text-gray'
                >
                  {t(
                    'waitingForSignature.hashProcess.completed.manualRedirectHint'
                  )}
                </Text>
              </div>
            )}
          </div>
        </MainContentLayout>
      </Content>
    </div>
  )
}
